import { apiV2 } from '../lib/baseAxios';

export interface SpeakerIdentity {
  _id: string;
  teamId: string;
  canonicalName: string;
  aliases: string[];
  embedding?: number[];
  totalOccurrences: number;
  recordingIds?: string[];
  clusterSize?: number;
  confidence?: string;
  embeddingModel?: string;
  embeddingDimensions?: number;
  createdAt: string;
  updatedAt: string;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  details?: any;
}

interface UpdateCanonicalNameResponse {
  message: string;
  speakerIdentity: SpeakerIdentity;
  previousCanonicalName: string;
  newCanonicalName: string;
}

/**
 * Create a new speaker identity
 */
export const createSpeakerIdentity = async (
  teamId: string,
  canonicalName: string,
  aliases?: string[],
): Promise<
  ApiResponse<{ message: string; speakerIdentity: SpeakerIdentity }>
> => {
  try {
    const response = await apiV2.post(`/speaker-identities/${teamId}`, {
      canonicalName,
      aliases: aliases || [],
    });
    return response.data;
  } catch (error) {
    console.error('Error creating speaker identity:', error);
    throw error;
  }
};

/**
 * Get all speaker identities for a team
 */
export const getSpeakerIdentities = async (
  teamId: string,
): Promise<ApiResponse<SpeakerIdentity[]>> => {
  try {
    const response = await apiV2.get(`/speaker-identities/${teamId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching speaker identities:', error);
    throw error;
  }
};

/**
 * Get a specific speaker identity with its aliases
 */
export const getSpeakerIdentity = async (
  teamId: string,
  speakerId: string,
): Promise<ApiResponse<SpeakerIdentity>> => {
  try {
    const response = await apiV2.get(
      `/speaker-identities/${teamId}/${speakerId}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching speaker identity:', error);
    throw error;
  }
};

/**
 * Get speaker identity by speaker name (canonical name or alias)
 */
export const getSpeakerIdentityByName = async (
  teamId: string,
  speakerName: string,
): Promise<ApiResponse<SpeakerIdentity>> => {
  try {
    const response = await apiV2.get(
      `/speaker-identities/${teamId}/speaker/${encodeURIComponent(speakerName)}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching speaker identity by name:', error);
    throw error;
  }
};

/**
 * Update the canonical name of a speaker identity
 */
export const updateCanonicalName = async (
  teamId: string,
  speakerId: string,
  newCanonicalName: string,
): Promise<ApiResponse<UpdateCanonicalNameResponse>> => {
  try {
    const response = await apiV2.put(
      `/speaker-identities/${teamId}/${speakerId}/canonical-name`,
      { newCanonicalName },
    );
    return response.data;
  } catch (error) {
    console.error('Error updating canonical name:', error);
    throw error;
  }
};

/**
 * Add a new alias to a speaker identity
 */
export const addAlias = async (
  teamId: string,
  speakerId: string,
  alias: string,
): Promise<
  ApiResponse<{ message: string; speakerIdentity: SpeakerIdentity }>
> => {
  try {
    const response = await apiV2.post(
      `/speaker-identities/${teamId}/${speakerId}/aliases`,
      { alias },
    );
    return response.data;
  } catch (error) {
    console.error('Error adding alias:', error);
    throw error;
  }
};

/**
 * Remove an alias from a speaker identity
 */
export const removeAlias = async (
  teamId: string,
  speakerId: string,
  alias: string,
): Promise<
  ApiResponse<{ message: string; speakerIdentity: SpeakerIdentity }>
> => {
  try {
    const response = await apiV2.delete(
      `/speaker-identities/${teamId}/${speakerId}/aliases/${encodeURIComponent(alias)}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error removing alias:', error);
    throw error;
  }
};

interface SplitSpeakerResponse {
  message: string;
  sourceSpeaker: SpeakerIdentity;
  newSpeaker: SpeakerIdentity;
}

/**
 * Split a speaker identity into two separate identities
 */
export const splitSpeaker = async (
  teamId: string,
  sourceSpeakerId: string,
  aliasesToMove: string[],
  newCanonicalName: string,
): Promise<ApiResponse<SplitSpeakerResponse>> => {
  try {
    const response = await apiV2.post(`/speaker-identities/${teamId}/split`, {
      sourceSpeakerId,
      aliasesToMove,
      newCanonicalName,
    });
    return response.data;
  } catch (error) {
    console.error('Error splitting speaker:', error);
    throw error;
  }
};

interface MergeSpeakersResponse {
  message: string;
  mergedSpeaker: SpeakerIdentity;
  deletedSpeakerIds: string[];
}

/**
 * Merge multiple speaker identities into one
 */
export const mergeSpeakers = async (
  teamId: string,
  speakerIds: string[],
  targetCanonicalName: string,
): Promise<ApiResponse<MergeSpeakersResponse>> => {
  try {
    const response = await apiV2.post(`/speaker-identities/${teamId}/merge`, {
      speakerIds,
      targetCanonicalName,
    });
    return response.data;
  } catch (error) {
    console.error('Error merging speakers:', error);
    throw error;
  }
};
